import React, { useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import styled from 'styled-components';

import AppContext from '../contexts/AppContext';
import ThemeContext from '../contexts/ThemeContext';
import FunctionsContext from '../contexts/FunctionsContext';

import withLeftSideBar from '../hocs/withLeftSideBar';
import IconButton from '../components/IconButton';
import Spinner from '../components/Spinner';
import Icon from '../components/Icon';
import {
  MainHeader,
  MainContentTopBar,
  ContainerWrapper,
} from '../components/basicComponents';
import {
  TwoColumnLayout,
  FormContent,
  StepContainer,
} from '../components/PrebookingSharedComponents';
import { CardContainer, SectionTitle, FieldGroup, Field, FieldLabel, FieldValue, getDisplayScope } from '../components/translationOrder/SummaryShared';
import SummaryLanguageRow from '../components/translationOrder/SummaryLanguageRow';
import SummaryDeliveryDate from '../components/translationOrder/SummaryDeliveryDate';
import SummaryInstructions from '../components/translationOrder/SummaryInstructions';
import SummaryNote from '../components/translationOrder/SummaryNote';
import SummaryMaterials from '../components/translationOrder/SummaryMaterials';
import SummaryEstimatedTotal from '../components/translationOrder/SummaryEstimatedTotal';
import SummaryCustomerInfo from '../components/translationOrder/SummaryCustomerInfo';
import SummaryInvoicing from '../components/translationOrder/SummaryInvoicing';
import TranslatedMaterials from '../components/translationOrder/TranslatedMaterials';

import i18n from '../i18n';
import { getTranslation, getInterpreterTranslation, downloadTranslationSourceFiles, completeTranslation } from '../services/translationsService';
import { cancelTranslationOrder } from '../services/translationOrderService';
import { getAttachmentDownloadUrl } from '../services/prebookService';
import { downloadFileWithProgress } from '../services/fileDownloadService';
import useFileTransferProgress from '../hooks/useFileTransferProgress';
import { showError, showSuccess } from '../services/toastService';
import { PATH_ROUTES } from '../utils/pathRoutes';
import { formatDate } from '../utils/dateUtils';
import { TRANSLATION_STATUS } from '../utils/translationStatus';
import { canAccessTranslations } from '../utils/translationAccess';

const StyledContainer = styled.div`
  display: flex;
  flex: 1;
  height: 100%;
  flex-direction: column;
`;

const ContentWrapper = styled.div`
  max-width: 900px;
  width: 100%;
  margin: 0 auto;
  padding: 24px 16px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
`;

const CancelButtonWrapper = styled(ButtonWrapper)``;

const CancelNotPossibleMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: 520px;
  padding: 12px 16px;
  border: 1px solid ${({ colors }) => colors.borderColor};
  border-radius: 8px;
  background: ${({ colors }) => colors.hoverColor};
  color: ${({ colors }) => colors.secondaryColor};
  font-size: 14px;
  line-height: 1.5;
`;

const CancelOrderButton = styled.button`
  padding: 12px 32px;
  border-radius: 25px;
  border: none;
  background: ${({ colors }) => colors.tulkaMainColor};
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:hover { opacity: 0.8; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const OrderTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: ${({ colors }) => colors.webDarkBlue};
  margin: 0 0 16px 0;
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: none;
  background: ${({ colors }) => colors.tulkaMainColor};
  color: white;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  margin-top: 12px;
  transition: opacity 0.2s;
  align-self: flex-start;

  &:hover { opacity: 0.8; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const CompletedFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 24px;
`;

const CompletedStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ colors }) => colors.tulkaMainColor};
  font-size: 14px;
  font-weight: 600;
`;

const CheckIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${({ colors }) => colors.tulkaMainColor};
  color: white;
  font-size: 12px;
`;

const SectionDivider = styled.hr`
  border: none;
  border-top: 1px solid ${({ colors }) => colors.borderColor};
  margin: 24px 0;
  width: 100%;
`;

const PreviewOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  z-index: 1001;
`;

const PreviewHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  background-color: rgba(0, 0, 0, 0.9);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const PreviewTitle = styled.h3`
  margin: 0;
  color: white;
  font-size: ${({ layout }) => layout.fontSize}px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: calc(100% - 60px);
`;

const PreviewCloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const PreviewContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  overflow: auto;
`;

const PreviewIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  background-color: white;
  border-radius: 8px;
`;

const TranslationDetail = ({ toggleSideBar, sidebarDocked, ...rest }) => {
  const { id } = useParams();
  const history = useHistory();
  const { user } = useContext(AppContext);
  const { colors, layout } = useContext(ThemeContext);
  const { sessionExpired, setBusy } = useContext(FunctionsContext);

  const [loading, setLoading] = useState(true);
  const [translation, setTranslation] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewFilename, setPreviewFilename] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const downloadProgress = useFileTransferProgress();

  const isInterpreter = user?.type === 'Interpreter';
  const isCustomer = !isInterpreter;
  // Interpreters have no userGroup flag and must keep access to their orders;
  // the gate applies to customers only.
  const translationEnabled = isInterpreter || canAccessTranslations(user);

  // Customers who wouldn't see Translations in the sidebar shouldn't reach a
  // translation even by typing the URL directly — send them home.
  useEffect(() => {
    if (!translationEnabled) {
      history.replace(PATH_ROUTES.home);
    }
  }, [translationEnabled, history]);

  useEffect(() => {
    if (!translationEnabled) {
      return undefined;
    }
    const abortController = new AbortController();
    let cancelled = false;
    const fetchData = async () => {
      try {
        const fetchFn = isInterpreter ? getInterpreterTranslation : getTranslation;
        const data = await fetchFn(id, { signal: abortController.signal });
        if (!cancelled) {
          setTranslation(data);
        }
      } catch (e) {
        if (cancelled || e.name === 'AbortError') return;
        if (e.message === 'Session expired') {
          sessionExpired();
        } else {
          showError(i18n('requestErrorMessage'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    fetchData();
    return () => {
      cancelled = true;
      abortController.abort();
    };
  }, [id, isInterpreter, sessionExpired, translationEnabled]);

  const handleCancelOrder = useCallback(async () => {
    if (!id || isCancelling) return;

    setIsCancelling(true);
    setBusy(true);
    try {
      await cancelTranslationOrder(id);
      history.replace(PATH_ROUTES.translations);
    } catch (error) {
      console.error('Failed to cancel translation order:', error);
      showError(error.message || i18n('translation_order_cancel_failed'));
    } finally {
      setIsCancelling(false);
      setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, history, setBusy]);

  const handlePreview = useCallback(async (att) => {
    try {
      if (isInterpreter) {
        const data = await downloadTranslationSourceFiles(id);
        if (data?.sourceFiles) {
          const match = data.sourceFiles.find((f) => f.filename === att.filename);
          if (match?.downloadUrl) {
            setPreviewFilename(att.filename);
            setPreviewUrl(match.downloadUrl);
          }
        }
      } else {
        const response = await getAttachmentDownloadUrl(id, att.id);
        if (response?.downloadUrl) {
          setPreviewFilename(att.filename);
          setPreviewUrl(response.downloadUrl);
        }
      }
    } catch (err) {
      console.error('Failed to load preview:', err);
      if (err.message === 'Session expired') {
        sessionExpired();
      } else {
        showError(i18n('attachment_preview_error') || 'Failed to load preview');
      }
    }
  }, [id, isInterpreter, sessionExpired]);

  const downloadTimersRef = useRef([]);

  useEffect(() => {
    return () => {
      downloadTimersRef.current.forEach(clearTimeout);
      downloadTimersRef.current = [];
    };
  }, []);

  const downloadFiles = useCallback((files) => {
    downloadTimersRef.current.forEach(clearTimeout);
    downloadTimersRef.current = [];
    files.forEach((file, index) => {
      if (!file.downloadUrl) return;
      const key = file.key || file.filename || `download-${index}`;
      const timerId = setTimeout(async () => {
        downloadProgress.start(key);
        try {
          await downloadFileWithProgress(file.downloadUrl, {
            filename: file.filename || 'download',
            onProgress: (loaded, total) => downloadProgress.update(key, loaded, total),
          });
          downloadProgress.complete(key);
        } catch (err) {
          console.error('Download failed:', err);
          downloadProgress.fail(key);
          showError(i18n('requestErrorMessage'));
        }
      }, index * 500);
      downloadTimersRef.current.push(timerId);
    });
  }, [downloadProgress]);

  const handleSingleFileDownload = useCallback(async (att) => {
    try {
      if (isInterpreter) {
        const data = await downloadTranslationSourceFiles(id);
        if (data?.sourceFiles) {
          const match = data.sourceFiles.find((f) => f.filename === att.filename);
          if (match?.downloadUrl) {
            downloadFiles([{ ...match, filename: att.filename, key: att.id }]);
          }
        }
      } else {
        const response = await getAttachmentDownloadUrl(id, att.id);
        if (response?.downloadUrl) {
          downloadFiles([{ downloadUrl: response.downloadUrl, filename: att.filename, key: att.id }]);
        }
      }
    } catch (e) {
      if (e.message === 'Session expired') {
        sessionExpired();
      } else {
        showError(e.message || i18n('requestErrorMessage'));
      }
    }
  }, [id, isInterpreter, downloadFiles, sessionExpired]);

  const handleClosePreview = useCallback(() => {
    setPreviewUrl(null);
    setPreviewFilename('');
  }, []);

  const handleTranslatedFilePreview = useCallback(async (file) => {
    try {
      const response = await getAttachmentDownloadUrl(id, file._id);
      if (response?.downloadUrl) {
        setPreviewFilename(file.filename);
        setPreviewUrl(response.downloadUrl);
      }
    } catch (err) {
      console.error('Failed to load translated file preview:', err);
      if (err.message === 'Session expired') {
        sessionExpired();
      } else {
        showError(i18n('attachment_preview_error') || 'Failed to load preview');
      }
    }
  }, [id, sessionExpired]);

  const handleTranslatedFileDownload = useCallback(async (file) => {
    try {
      const response = await getAttachmentDownloadUrl(id, file._id);
      if (response?.downloadUrl) {
        downloadFiles([{ downloadUrl: response.downloadUrl, filename: file.filename, key: file._id }]);
      }
    } catch (err) {
      if (err.message === 'Session expired') {
        sessionExpired();
      } else {
        showError(err.message || i18n('requestErrorMessage'));
      }
    }
  }, [id, downloadFiles, sessionExpired]);

  const refreshTranslation = useCallback(async () => {
    try {
      const fetchFn = isInterpreter ? getInterpreterTranslation : getTranslation;
      const data = await fetchFn(id);
      setTranslation(data);
    } catch (e) {
      if (e.message === 'Session expired') {
        sessionExpired();
      } else {
        showError(i18n('requestErrorMessage'));
      }
    }
  }, [id, isInterpreter, sessionExpired]);

  // Map API data to the shape expected by SummaryMaterials
  // Backend stores 'seconds' but UI shows "minutes" — convert (and round) when reading
  // so an admin-entered second count renders as a clean value, not 4.166666666666667.
  const attachments = useMemo(() => (translation?.sourceFiles || []).map(file => ({
    id: file._id,
    filename: file.filename,
    fileSize: file.fileSize,
    contentType: file.contentType,
    estimatedScope: getDisplayScope(file.unitScope, file.unitType),
    scopeType: file.unitType,
    canPreview: true,
  })), [translation?.sourceFiles]);

  const handleDownloadMaterial = useCallback(async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      const data = await downloadTranslationSourceFiles(id);
      if (data?.sourceFiles) {
        // Key each source file to its attachment id so the progress bar lands on
        // the matching row; fall back to the filename when no id is found.
        downloadFiles(data.sourceFiles.map((sf) => ({
          ...sf,
          key: (attachments.find((a) => a.filename === sf.filename) || {}).id || sf.filename,
        })));
      }
    } catch (e) {
      if (e.message === 'Session expired') {
        sessionExpired();
      } else {
        showError(e.message || i18n('requestErrorMessage'));
      }
    } finally {
      setIsDownloading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, downloadFiles, sessionExpired, attachments]);

  const handleMarkCompleted = useCallback(async () => {
    if (isCompleting) return;
    setIsCompleting(true);
    setBusy(true);
    try {
      const result = await completeTranslation(id);
      showSuccess(i18n('translation_mark_completed_success'));
      setTranslation((prev) => ({
        ...prev,
        ...result,
        status: TRANSLATION_STATUS.COMPLETED,
        translatorCompletedAt: result?.translatorCompletedAt || new Date().toISOString(),
      }));
    } catch (error) {
      if (error.message === 'Session expired') {
        sessionExpired();
      } else {
        showError(error.message || i18n('translation_mark_completed_failed'));
      }
    } finally {
      setIsCompleting(false);
      setBusy(false);
    }
  }, [id, isCompleting, setBusy, sessionExpired]);

  // Required translator authorization (TUL-5330): resolve the order's levelId
  // to its localized name from the user payload, like the order wizard does.
  // Interpreters (and older backends) carry no translationLevels, so the row
  // simply stays hidden for them.
  const levelName = useMemo(() => (
    (user?.translationLevels || []).find((level) => level.id === translation?.levelId)?.name || null
  ), [user, translation]);

  // Map API data to the shape expected by SummaryCustomerInfo
  const customerInfoData = useMemo(() => translation ? {
    customerNumber: user?.phoneNumber || '-',
    orderMadeBy: translation.customerEmail || '-',
    orderingUnit: translation.customerName || '-',
  } : {}, [translation, user]);

  // Map API data to the shape expected by SummaryInvoicing
  const invoicingData = useMemo(() => translation ? {
    costAllocationGroup: translation.invoiceUserGroupName
      ? { name: translation.invoiceUserGroupName }
      : null,
    additionalInvoicingInfo: translation.invoiceRef || '-',
    ccEmails: Array.isArray(translation.ccEmails)
      ? translation.ccEmails
      : (translation.ccEmails
        ? translation.ccEmails.split(',').map((e) => e.trim()).filter(Boolean)
        : []),
  } : {}, [translation]);

  if (!translationEnabled) {
    return null;
  }

  // Translators work against the admin-set delivery deadline; customers see
  // their own due date (TUL-6080).
  const shownDueDate = (isInterpreter && translation?.deliveryDeadline) || translation?.dueDate;

  return (
    <StyledContainer {...rest}>
      <MainContentTopBar borderBottomColor={colors.webDeviderColor}>
        {sidebarDocked ? null : (
          <IconButton
            icon={'menu'}
            iconLib={'material'}
            iconColor={colors.tulkaMainColor}
            onClick={toggleSideBar}
          />
        )}
        <IconButton
          icon={'arrow_back'}
          iconLib={'material'}
          iconColor={colors.tulkaMainColor}
          onClick={() => history.push(PATH_ROUTES.translations)}
        />
        <MainHeader
          sidebarDocked={sidebarDocked}
          padding={layout.padding}
          color={colors.webDarkBlue}
        >
          {i18n(isCustomer ? 'back' : 'translation_order')}
        </MainHeader>
      </MainContentTopBar>
      <ContainerWrapper>
        {loading ? (
          <Spinner overlay />
        ) : translation ? (
          <ContentWrapper>
            {isInterpreter && translation.orderNumber && (
              <OrderTitle colors={colors}># {translation.orderNumber}</OrderTitle>
            )}
            <StepContainer>
              {/* Details Section */}
              <CardContainer>
                <TwoColumnLayout>
                  <SectionTitle>{i18n('translation_details')}</SectionTitle>
                  <FormContent>
                    <FieldGroup>
                      {isCustomer && translation.orderNumber && (
                        <Field>
                          <FieldLabel>{i18n('translation_order_order_number')}</FieldLabel>
                          <FieldValue>{translation.orderNumber}</FieldValue>
                        </Field>
                      )}
                      <SummaryLanguageRow
                        fromLanguage={{ name: translation.sourceLanguageNameEn }}
                        toLanguage={{ name: translation.targetLanguageNameEn }}
                      />
                      {levelName && (
                        <Field>
                          <FieldLabel>{i18n('translation_order_translator_type')}</FieldLabel>
                          <FieldValue>{levelName}</FieldValue>
                        </Field>
                      )}
                      <SummaryDeliveryDate
                        date={shownDueDate ? new Date(shownDueDate) : null}
                      />
                      <SummaryInstructions
                        instructions={translation.customerInstructions}
                      />
                      <SummaryNote
                        label={i18n('translation_order_message_to_translator')}
                        value={translation.adminComments}
                      />
                      {/* Internal CS note for the translator — never shown to the customer (TUL-5168) */}
                      {isInterpreter && (
                        <SummaryNote
                          label={i18n('translation_order_customer_service_instructions')}
                          value={translation.customerServiceInstruction}
                        />
                      )}
                      {isInterpreter && (
                        <SummaryEstimatedTotal
                          attachments={attachments}
                        />
                      )}
                      <SummaryMaterials
                        attachments={attachments}
                        onPreview={handlePreview}
                        onDownload={handleSingleFileDownload}
                        simplified={isInterpreter}
                        progress={downloadProgress.progress}
                      />
                      {isInterpreter && (
                        <ActionButton
                          colors={colors}
                          onClick={handleDownloadMaterial}
                          disabled={isDownloading}
                        >
                          <Icon icon="download" iconLib="material" iconColor="white" iconSize={18} />
                          {isDownloading ? '...' : i18n('translation_download_material')}
                        </ActionButton>
                      )}
                      {translation.translatedFiles && translation.translatedFiles.length > 0
                        // Customers only see translated materials once the order is delivered.
                        && (isInterpreter || translation.status === TRANSLATION_STATUS.DELIVERED) && (
                      <>
                        <SectionDivider colors={colors} />
                        <TranslatedMaterials
                          translationId={id}
                          translatedFiles={translation.translatedFiles}
                          onRefresh={refreshTranslation}
                          readOnly={isCustomer}
                          onDownload={handleTranslatedFileDownload}
                          onPreview={handleTranslatedFilePreview}
                          downloadProgress={downloadProgress.progress}
                        />
                      </>
                    )}
                    {isInterpreter && (!translation.translatedFiles || translation.translatedFiles.length === 0) && (
                      <>
                        <SectionDivider colors={colors} />
                        <TranslatedMaterials
                          translationId={id}
                          translatedFiles={[]}
                          onRefresh={refreshTranslation}
                          onDownload={handleTranslatedFileDownload}
                          onPreview={handleTranslatedFilePreview}
                          downloadProgress={downloadProgress.progress}
                        />
                      </>
                    )}
                    </FieldGroup>
                  </FormContent>
                </TwoColumnLayout>
              </CardContainer>

              {/* Customer Information Section */}
              {isCustomer && (
                <SummaryCustomerInfo formData={customerInfoData} />
              )}

              {/* Invoicing Section */}
              {isCustomer && (
                <SummaryInvoicing formData={invoicingData} />
              )}
            </StepContainer>

            {/* Mark as Completed / Completed State */}
            {isInterpreter && !translation.translatorCompletedAt && translation.translatedFiles?.length > 0 && (
              <ButtonWrapper>
                <CancelOrderButton colors={colors} onClick={handleMarkCompleted} disabled={isCompleting}>
                  {isCompleting ? '...' : i18n('translation_mark_completed')}
                </CancelOrderButton>
              </ButtonWrapper>
            )}
            {isInterpreter && !!translation.translatorCompletedAt && (
              <CompletedFooter>
                <CompletedStatus colors={colors}>
                  <CheckIcon colors={colors}>&#10003;</CheckIcon>
                  {i18n('translation_task_completed')} {formatDate(translation.translatorCompletedAt)}
                </CompletedStatus>
              </CompletedFooter>
            )}

            {/* Cancel Order Button - only available while the order is still new */}
            {isCustomer && translation.status === TRANSLATION_STATUS.NEW_ORDER && (
              <CancelButtonWrapper>
                <CancelOrderButton
                  colors={colors}
                  onClick={handleCancelOrder}
                  disabled={isCancelling}
                >
                  {i18n('translation_order_cancel_order')}
                </CancelOrderButton>
              </CancelButtonWrapper>
            )}
            {isCustomer && [TRANSLATION_STATUS.ASSIGNED, TRANSLATION_STATUS.IN_PROGRESS].includes(translation.status) && (
              <CancelButtonWrapper>
                <CancelNotPossibleMessage colors={colors}>
                  <Icon
                    icon="info"
                    iconLib="material"
                    iconColor={colors.secondaryColor}
                    iconSize={20}
                  />
                  <span>{i18n('translation_order_cancel_not_possible')}</span>
                </CancelNotPossibleMessage>
              </CancelButtonWrapper>
            )}
          </ContentWrapper>
        ) : null}
      </ContainerWrapper>

      {/* Preview Modal */}
      {previewUrl && (
        <PreviewOverlay
          role="dialog"
          aria-modal="true"
          onClick={handleClosePreview}
        >
          <PreviewHeader onClick={(e) => e.stopPropagation()}>
            <PreviewTitle layout={layout}>{previewFilename}</PreviewTitle>
            <PreviewCloseButton
              onClick={handleClosePreview}
              aria-label={i18n('close') || 'Close'}
            >
              <Icon
                icon="close"
                iconLib="material"
                iconColor="white"
                iconSize={24}
              />
            </PreviewCloseButton>
          </PreviewHeader>
          <PreviewContent onClick={(e) => e.stopPropagation()}>
            <PreviewIframe
              src={`${previewUrl}#toolbar=0&navpanes=0`}
              title={previewFilename}
            />
          </PreviewContent>
        </PreviewOverlay>
      )}
    </StyledContainer>
  );
};

export default withLeftSideBar(TranslationDetail);
